import * as Sentry from "@sentry/react";
import type { Router } from "@tanstack/react-router";

function getTracesSampleRate(): number {
  const env = import.meta.env.VITE_ENVIRONMENT || "local";
  switch (env) {
    case "production":
      return 0.1;
    case "preview":
      return 0.5;
    default:
      return 1.0;
  }
}

function getReplaysSampleRate(): number {
  const env = import.meta.env.VITE_ENVIRONMENT || "local";
  switch (env) {
    case "production":
      return 0.1;
    case "preview":
      return 0.5;
    default:
      return 1.0;
  }
}

// Chrome/Edge's bare network failure. Anchored (not a substring) so it can't
// swallow genuine app errors whose message merely starts with "Failed to fetch"
// (e.g. ApiError "Failed to fetch account details"). Sentry's fetch
// instrumentation appends the request URL — "Failed to fetch (https://host/...)"
// — which the optional " (...)" group still matches.
const FAILED_TO_FETCH_RE = /^Failed to fetch( \(.+\))?$/;

// Safari's bare network failure. Anchored (not a substring) so it can't swallow
// genuine app errors whose message merely contains "Load failed" (e.g.
// "Profile load failed"). Same optional " (...)" group as above matches any
// URL-suffixed form Sentry may produce.
const LOAD_FAILED_RE = /^Load failed( \(.+\))?$/;

// Transient network + stale-chunk error messages we never want to report.
// Matched as substrings by Sentry's InboundFilters (ignoreErrors) and by
// filterSentryEvent below. Each is specific enough that a substring match
// won't collide with our own thrown error messages.
const NETWORK_ERROR_MESSAGES = [
  "Importing a module script failed",
  "Failed to fetch dynamically imported module",
  "Failed to update a ServiceWorker",
];

/**
 * Decide whether a Sentry event is dropped (return null) or kept.
 *
 * Network errors are matched on the parsed event value (event.exception),
 * NOT on hint.originalException.message: the original thrown object may be
 * rebuilt by the SDK rather than remaining a real TypeError instance, so the
 * serialized event value is the only reliable surface. CancelledError and
 * 401/403 checks read hint.originalException because those are our own thrown
 * errors with a known shape.
 *
 * Exported for unit testing (see instrument.test.ts).
 */
export function filterSentryEvent(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
): Sentry.ErrorEvent | null {
  const err = hint.originalException;

  // React Query CancelledError — normal when components unmount before query completes
  if (
    err &&
    typeof err === "object" &&
    "name" in err &&
    (err as { name: string }).name === "CancelledError"
  ) {
    return null;
  }

  // ApiError with expected status codes — handled by UI (redirect to login, access denied)
  if (err && typeof err === "object" && "status" in err) {
    const status = (err as { status: number }).status;
    if (status === 401 || status === 403) return null;
  }

  // Transient network + stale-chunk errors — not actionable.
  const value = event.exception?.values?.[0]?.value ?? "";
  if (
    FAILED_TO_FETCH_RE.test(value) ||
    LOAD_FAILED_RE.test(value) ||
    NETWORK_ERROR_MESSAGES.some((m) => value.includes(m)) ||
    value.includes("NetworkError when attempting to fetch")
  ) {
    return null;
  }

  return event;
}

// biome-ignore lint/suspicious/noExplicitAny: TanStack Router generic params are irrelevant for Sentry integration
export function initSentry(router: Router<any, any, any, any>) {
  const environment = import.meta.env.VITE_ENVIRONMENT || "local";

  // Skip Sentry in local dev to avoid noise
  if (environment === "local") return;

  Sentry.init({
    dsn: "https://a55804d928bf79ba8b2718e4ad592c5d@o4511133905256448.ingest.us.sentry.io/4511134324228096",
    environment,
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: getTracesSampleRate(),
    replaysSessionSampleRate: getReplaysSampleRate(),
    replaysOnErrorSampleRate: 1.0,
    // Primary gate: InboundFilters substring-matches these against the event
    // value before beforeSend runs. filterSentryEvent is defense-in-depth.
    ignoreErrors: [
      FAILED_TO_FETCH_RE,
      LOAD_FAILED_RE,
      ...NETWORK_ERROR_MESSAGES,
      /NetworkError when attempting to fetch/,
    ],
    beforeSend: filterSentryEvent,
  });
}
